/**
 * Every word an extracted criterion's points say — Story 20.8.
 *
 * Kept apart by WHERE EACH WORD COMES FROM, as `extraction-copy.ts` does one card
 * down, because `tests/points-copy.test.ts` holds each kind to a different source:
 *
 *   DRAWN     `design/screens/editor-qualify.html`'s `points` capture draws it —
 *             Experience's ranges, and the One of, Yes/no and Text specimens.
 *             Held to the mockup, verbatim, including the inputs' own names.
 *   COMPOSED  no capture reaches it: a range with an open start, points a field
 *             can no longer award, and a range whose ends are the wrong way round.
 *             Built on the drawn sentences' own shape, and listed here so a
 *             round-trip replaces them in one place.
 *
 * All of it is held to RULE-021: the points say what a reading is WORTH, in the
 * operator's numbers, and never whether an answer is good.
 *
 * NO RUNTIME IMPORTS, so a node-side test can load this file without resolving
 * the workspace.
 */

export const DRAWN = {
  /** The panel's heading: the criterion's weight is its ceiling. */
  scoreOutOf: (weight: number): string => `Score out of ${weight}`,
  from: 'From',
  to: 'to',
  /** The open end's placeholder. */
  noLimit: 'No limit',
  ranges: 'Ranges are checked in order. A value on a boundary takes the first range it falls in.',
  addRange: 'Add a range',
  /** The last row of a One of, a Yes/no and a Number: the model read nothing. */
  nothingRead: 'Nothing read',
  yes: 'Yes',
  no: 'No',
  mentioned: 'Mentioned',
  notMentioned: 'Not mentioned',

  // The inputs' names, as the capture writes them.
  scoreFor: (reading: string): string => `Score for ${reading}`,
  scoreWhenNothing: 'Score when nothing is read',
  rangeFrom: (ordinal: string): string => `${ordinal} range from`,
  rangeTo: (ordinal: string): string => `${ordinal} range to`,
  scoreBetween: (from: number, to: number): string => `Score from ${from} to ${to}`,
  scoreUpwards: (from: number): string => `Score from ${from} upwards`,
} as const

/** "First" to "Third" are drawn; the rest follow them. */
export const ORDINALS = [
  'First',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eighth',
  'Ninth',
  'Tenth',
] as const

export const COMPOSED = {
  /** A range past the tenth. */
  ordinal: (n: number): string => `Range ${n}`,
  /** A range with no start, drawn nowhere: the capture's first range starts at 0. */
  scoreUpTo: (to: number): string => `Score up to ${to}`,
  /** A range with neither end: every number it reads falls in it. */
  scoreAny: 'Score for any number',
  /**
   * Points kept for a reading the field no longer gives — a value taken off a One
   * of, or a field whose type changed underneath its criterion. DECISION-067's
   * shape: a key that matches nothing scores zero, and says nothing.
   */
  strandedOne: (key: string): string =>
    `${key} is no longer one of this field's readings, so its points never count. Changing a score here removes it.`,
  strandedMany: (keys: string): string =>
    `${keys} are no longer among this field's readings, so their points never count. Changing a score here removes them.`,
  /** A range whose start is above its end, which nothing can fall in. */
  backwards: 'From is above to, so nothing falls in this range. Swap them, or clear one.',
} as const
